import React from 'react';
import { Navigate } from 'react-router-dom';

import { LoadingSpinner } from '../LoadingSpinner/LoadingSpinner';
import { ErrorMessage } from '../ErrorMessage/ErrorMessage';
import { useAuth } from '../../hooks/useAuthFixed';
import { useUserProfile } from '../../hooks/api';
import { PatientProvider } from '../../contexts/PatientContext';
import { usePatient } from '../../hooks';
import { CommunicationConsentGate } from '../CommunicationConsentGate/CommunicationConsentGate';

export type AppReadyMode = 'requireProfile' | 'authOnly' | 'authOnlySkipConsent';

export interface AppReadyGateProps {
  /**
   * - 'requireProfile': auth + 通信の秘密同意 + profile(completed=true) + linkedPatients が揃うまで待つ（メイン画面用）
   * - 'authOnly': auth + 通信の秘密同意 のみ待ち、profile/linkedPatients は待たない（/menu/profile 用）
   * - 'authOnlySkipConsent': auth のみ。同意ゲートを通さない（/consent 画面自身がゲート回避ポイント）
   */
  mode: AppReadyMode;
  children: React.ReactNode;
}

/**
 * 単一のローディング DOM（同一 props の LoadingSpinner）。
 * 段階遷移しても React の差分更新により Spinner DOM/アニメーションが連続する。
 */
const ReadyLoading: React.FC = () => (
  <LoadingSpinner fullPage message="読み込み中..." />
);

/**
 * Auth 待ち。
 *
 * 遷移方針:
 * - `isLoading`: `ReadyLoading`（他ゲートと同一 DOM で連続表示）
 * - `error != null`（LIFF 初期化失敗 / verifyLineAuth 失敗 等）:
 *   `ErrorMessage` + 再読み込みボタンで明示表示。招待トークンを持たないユーザが
 *   `/link_token` の空フォームに飛ばされて詰むのを防ぐ（PR-3 レビュー warning 指摘 /
 *   旧 useLineLoginGuard の liffError 表示相当を復元）
 * - `!isAuthenticated`: `/link_token` へリダイレクト
 *
 * `error` かつ `!isAuthenticated` のケース（トークン検証失敗など）は
 * ErrorMessage を優先する。無限リダイレクトを避けつつ、ユーザーが原因を認識できる。
 */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading, error } = useAuth();

  if (isLoading) return <ReadyLoading />;
  if (error) {
    return (
      <ErrorMessage
        fullPage
        title="認証エラー"
        description={error}
        onRetry={() => window.location.reload()}
        retryLabel="再読み込み"
      />
    );
  }
  if (!isAuthenticated) return <Navigate to="/link_token" replace />;
  return <>{children}</>;
};

/**
 * Profile 完了待ち。未完了は /menu/profile へリダイレクト。
 * AuthGate の内側でのみ使う前提（isAuthenticated=true 想定）。
 */
const ProfileGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, isLoading } = useUserProfile();

  if (isLoading) return <ReadyLoading />;
  if (!data?.completed) return <Navigate to="/menu/profile" replace />;
  return <>{children}</>;
};

/**
 * linkedPatients 取得完了待ち。PatientProvider を内側で配置し、
 * その isLoading を観測して同一 Spinner で待つ。
 */
const PatientGate: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PatientProvider>
    <PatientGateInner>{children}</PatientGateInner>
  </PatientProvider>
);

const PatientGateInner: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoading } = usePatient();
  if (isLoading) return <ReadyLoading />;
  return <>{children}</>;
};

/**
 * アプリ準備ゲート。
 *
 * 役割:
 * - auth / 通信の秘密同意 / profile / linkedPatients の 4 軸のローディングを
 *   「同一 DOM の LoadingSpinner」で繋ぎ、段階点滅を 1 枚のスピナーに見せる。
 * - 各段階で必要なリダイレクトを担う。
 *
 * 使い分け:
 * - 'authOnlySkipConsent': /consent 画面自身。同意ゲートを回避（無限ループ防止）
 * - 'authOnly': /menu/profile（プロフィール設定画面）。auth + 通信の秘密同意 を待つ
 * - 'requireProfile': メイン画面群。全 4 段階を待ち、PatientProvider を内側に提供する
 */
export const AppReadyGate: React.FC<AppReadyGateProps> = ({ mode, children }) => {
  if (mode === 'authOnlySkipConsent') {
    return <AuthGate>{children}</AuthGate>;
  }
  if (mode === 'authOnly') {
    return (
      <AuthGate>
        <CommunicationConsentGate>{children}</CommunicationConsentGate>
      </AuthGate>
    );
  }
  return (
    <AuthGate>
      <CommunicationConsentGate>
        <ProfileGate>
          <PatientGate>{children}</PatientGate>
        </ProfileGate>
      </CommunicationConsentGate>
    </AuthGate>
  );
};

export default AppReadyGate;
